/**
 * The drip page's four account-wide actions, and what each confirmation says.
 *
 * Run Now, Backfill All and the pause switch each acted on a single click: one mis-tap on Run Now
 * sent the next email in every active sequence, and Backfill All enrolled every existing user.
 * Each now goes through ConfirmDialog, whose body states how many people it reaches, taken from
 * the stats the page already loads.
 */
export type DripBulkAction = 'run' | 'backfill' | 'pause' | 'resume';

export interface DripBulkCopy {
  title: string;
  body: string;
  actionLabel: string;
}

const people = (n: number) => `${n.toLocaleString('en-IN')} ${n === 1 ? 'person' : 'people'}`;

export function dripBulkCopy(action: DripBulkAction, active: number | null | undefined): DripBulkCopy {
  const reach = active == null ? 'every active enrolment' : people(active);
  switch (action) {
    case 'run':
      return {
        title: 'Send due emails now?',
        body: `Runs the drip processor immediately. Any step that is due is emailed now, across ${reach} in an active sequence. Sent emails cannot be recalled.`,
        actionLabel: 'Run now',
      };
    case 'backfill':
      return {
        title: 'Enrol every existing jobseeker?',
        // Matches DripCampaignService.backfillAllJobseekers: jobseekers only, email notifications
        // not turned off, already-enrolled skipped. Employers are not backfilled.
        body: 'Enrols every existing jobseeker who has email notifications on and is not already in a sequence. They start receiving drip emails on the normal schedule. Employers are not included.',
        actionLabel: 'Backfill all',
      };
    case 'pause':
      return {
        title: 'Pause all drip emails?',
        body: `No drip email is sent to anyone until you resume. This affects ${reach} in an active sequence. Enrolments are kept.`,
        actionLabel: 'Pause',
      };
    case 'resume':
      return {
        title: 'Resume drip emails?',
        body: `The scheduler starts sending again, and steps that fell due while paused go out on its next run (it picks up due and overdue steps). This affects ${reach} in an active sequence.`,
        actionLabel: 'Resume',
      };
  }
}
